import { useState } from "react";
import api, { apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { PageHeader, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { Plus, Pencil, Trash2, Store, Building2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nom: "", type: "boutique", adresse: "", actif: true };

export default function PointsDeVente() {
  const { data, loading, refetch } = useData("/pos");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ nom: p.nom, type: p.type, adresse: p.adresse || "", actif: p.actif }); setEditId(p.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/pos/${editId}`, form);
      else await api.post("/pos", form);
      toast.success(editId ? "Point de vente modifié" : "Point de vente créé");
      setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.nom} ?`)) return;
    try { await api.delete(`/pos/${p.id}`); toast.success("Supprimé"); refetch(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleActif = async (p) => {
    try {
      await api.put(`/pos/${p.id}`, { nom: p.nom, type: p.type, adresse: p.adresse || "", actif: !p.actif });
      toast.success(p.actif ? "Point de vente désactivé" : "Point de vente activé");
      refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // Le laboratoire n'est pas un point de vente : il est géré séparément.
  const posItems = (data || []).filter((p) => p.type !== "laboratoire");

  return (
    <div>
      <PageHeader title="Points de vente" subtitle="Ajoutez, modifiez ou désactivez vos boutiques. Le laboratoire est géré séparément.">
        <button className="tm-btn" onClick={openNew} data-testid="add-pos-btn"><Plus size={16} /> Nouveau point de vente</button>
      </PageHeader>

      {loading ? <Loader /> : posItems.length === 0 ? <Empty text="Aucun point de vente." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="pos-grid">
          {posItems.map((p) => (
            <div key={p.id} className="tm-card p-5 tm-fade" data-testid={`pos-card-${p.id}`} style={{ opacity: p.actif ? 1 : 0.55 }}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(212,175,55,.12)" }}>
                    {p.type === "distribution" ? <Building2 size={18} style={{ color: "var(--tm-gold)" }} /> : <Store size={18} style={{ color: "var(--tm-gold)" }} />}
                  </span>
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      {p.nom}
                      <span className="tm-badge" style={p.actif ? { background: "rgba(76,175,80,.15)", color: "#4CAF50" } : { background: "rgba(244,67,54,.15)", color: "#F44336" }}>
                        {p.actif ? "Actif" : "Inactif"}
                      </span>
                    </p>
                    <p className="text-xs text-[var(--tm-muted)] capitalize">{p.type === "distribution" ? "Distribution B2B" : "Boutique"}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(p)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)]" data-testid={`pos-edit-${p.id}`}><Pencil size={15} /></button>
                  <button onClick={() => remove(p)} className="text-[var(--tm-muted)] hover:text-[#F44336]" data-testid={`pos-delete-${p.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
              {p.adresse && <p className="text-sm text-[var(--tm-muted)] mt-3">{p.adresse}</p>}
              <button onClick={() => toggleActif(p)} className="tm-btn-ghost tm-btn w-full mt-3 text-sm" data-testid={`pos-toggle-${p.id}`}>
                {p.actif ? "Désactiver" : "Activer"}
              </button>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier le point de vente" : "Nouveau point de vente"} testid="pos-modal">
        <form onSubmit={save}>
          <Field label="Nom"><input className="tm-input" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="pos-nom" /></Field>
          <Field label="Type">
            <select className="tm-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="pos-type">
              <option value="boutique">Boutique / Stand (vente directe)</option>
              <option value="distribution">Distribution (B2B)</option>
            </select>
          </Field>
          <Field label="Adresse"><input className="tm-input" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></Field>
          <label className="flex items-center gap-2 mb-4 text-sm cursor-pointer">
            <input type="checkbox" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} data-testid="pos-actif" />
            Actif (visible dans les menus de sélection)
          </label>
          <button type="submit" className="tm-btn w-full mt-2" data-testid="pos-save">Enregistrer</button>
        </form>
      </Modal>
    </div>
  );
}
